import { useState, useEffect } from "react";

export interface MicrolinkPreviewState {
  imageUrl: string | null;
  siteTitle: string | null;
  loading: boolean;
  error: boolean;
}

/**
 * Recupera screenshot e titolo da Microlink API (generazione al momento della richiesta).
 * Fallback: immagine Open Graph se lo screenshot non è disponibile.
 */
export function useMicrolinkPreview(url: string | undefined): MicrolinkPreviewState {
  const [state, setState] = useState<MicrolinkPreviewState>({
    imageUrl: null,
    siteTitle: null,
    loading: Boolean(url?.trim()),
    error: false,
  });

  useEffect(() => {
    if (!url?.trim()) {
      setState({
        imageUrl: null,
        siteTitle: null,
        loading: false,
        error: false,
      });
      return;
    }

    const ac = new AbortController();

    (async () => {
      try {
        setState((s) => ({ ...s, loading: true, error: false }));
        const apiUrl = `https://api.microlink.io/?url=${encodeURIComponent(url.trim())}&screenshot=true&meta=true`;
        const res = await fetch(apiUrl, { signal: ac.signal });
        const json = (await res.json()) as {
          status?: string;
          data?: {
            title?: string;
            screenshot?: { url?: string };
            image?: { url?: string };
          };
        };

        if (ac.signal.aborted) return;

        if (json.status !== "success" || !json.data) {
          setState((s) => ({ ...s, loading: false, error: true }));
          return;
        }

        const screenshot = json.data.screenshot?.url;
        const ogImage = json.data.image?.url;
        const imageUrl = screenshot || ogImage || null;

        setState({
          imageUrl,
          siteTitle: json.data.title ?? null,
          loading: false,
          error: !imageUrl,
        });
      } catch {
        if (!ac.signal.aborted) {
          setState((s) => ({
            ...s,
            loading: false,
            error: true,
          }));
        }
      }
    })();

    return () => ac.abort();
  }, [url]);

  return state;
}
